'use client'

import * as React from 'react'
import { ArrowLeftIcon } from '@phosphor-icons/react'
import { Link } from '@tanstack/react-router'
import { PremiumSection } from '@/components/shared/PremiumSection'
import { PremiumHeading } from '@/components/shared/PremiumHeading'

type LegalLayoutProps = {
  title: string
  lastUpdated: string
  children: React.ReactNode
}

export function LegalLayout({
  title,
  lastUpdated,
  children,
}: LegalLayoutProps) {
  return (
    <div className="min-h-screen bg-[#0a0f0d] text-white">
      {/* Nav */}
      <nav className="fixed top-4 left-1/2 -translate-x-1/2 z-50 w-full max-w-5xl px-4">
        <div className="backdrop-blur-xl bg-black/40 border border-white/10 rounded-2xl px-6 py-3 flex items-center justify-between shadow-2xl shadow-black/50">
          <Link
            to="/"
            className="text-xl font-bold text-white tracking-tight hover:opacity-80 transition-opacity"
          >
            control
            <span className="text-[#80cca5]">stack</span>
            <span className="text-white/40">.ai</span>
          </Link>
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-sm font-medium text-white/60 hover:text-[#80cca5] transition-colors"
          >
            <ArrowLeftIcon className="size-4" />
            Back to Home
          </Link>
        </div>
      </nav>

      {/* Content */}
      <main className="pt-20">
        <PremiumSection>
          <div className="max-w-4xl mx-auto">
            <div className="mb-12">
              <PremiumHeading as="h1">{title}</PremiumHeading>
              <p className="text-slate-400 text-sm">
                Last updated: {lastUpdated}
              </p>
            </div>

            <div className="bg-white/[0.02] border border-white/10 backdrop-blur-xl rounded-3xl p-8 md:p-12 shadow-2xl">
              <article
                className="prose prose-invert prose-slate max-w-none 
                prose-headings:text-white prose-headings:font-bold 
                prose-p:text-slate-300 prose-p:leading-relaxed
                prose-strong:text-white prose-strong:font-semibold
                prose-ul:text-slate-300 prose-li:marker:text-[#80cca5]
                prose-a:text-[#80cca5] prose-a:no-underline hover:prose-a:underline
                prose-hr:border-white/10"
              >
                {children}
              </article>
            </div>
          </div>
        </PremiumSection>
      </main>

      {/* Footer */}
      <footer className="py-12 px-4 border-t border-white/10 bg-black/20">
        <div className="max-w-5xl mx-auto flex flex-col md:flex-row justify-between items-center gap-6 text-sm text-slate-500">
          <div className="font-medium">© 2026 controlstack.ai</div>
          <div className="flex gap-8">
            <Link
              to="/privacy"
              className="hover:text-[#80cca5] transition-colors font-medium"
            >
              Privacy
            </Link>
            <Link
              to="/terms"
              className="hover:text-[#80cca5] transition-colors font-medium"
            >
              Terms
            </Link>
            <Link
              to="/cookies"
              className="hover:text-[#80cca5] transition-colors font-medium"
            >
              Cookies
            </Link>
          </div>
        </div>
      </footer>
    </div>
  )
}
